import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox, FormGroup, ControlLabel } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import './AssetValidityFormComponent.css';

const TIME_FORMAT = 'HH:mm';
const DATE_FORMAT = `dd/MM/yyyy ${TIME_FORMAT}`;
const LEGACY_START_YEAR = 2010;
const LEGACY_END_YEAR = 2099;

/**
 * @typedef {object} ValidityFormComponentProps
 * @property {boolean} enabled Whether the validity period is enabled and the date pickers are rendered.
 * @property {function(boolean): void} setValidity Handles changes to the enable checkbox.
 * @property {Date} validityStart Selected start timestamp.
 * @property {function(Date): void} changeStartTs Handles start timestamp changes from the date picker.
 * @property {Date} validityEnd Selected end timestamp.
 * @property {function(Date): void} changeEndTs Handles end timestamp changes from the date picker.
 * @property {boolean} withPortal Whether to display the date picker in a portal. This fixes issues with the date picker being clipped by overflow:hidden containers, but may cause issues with modals and other components that use portals.
 */

/**
 * @param {ValidityFormComponentProps} props
 * @returns {JSX.Element}
 */
function ValidityFormComponent({
  enabled,
  setValidity,
  validityStart,
  changeStartTs,
  validityEnd,
  changeEndTs,
  withPortal,
}) {
  /*
   * need a local state to hold the date until it's valid,
   * otherwise the date picker gets into an invalid state
   * when an invalid date is selected and doesn't recover
   * when a valid date is selected again
   */
  const [localStartDate, setLocalStartDate] = React.useState(validityStart);
  const [localEndDate, setLocalEndDate] = React.useState(validityEnd);

  React.useEffect(() => {
    setLocalStartDate(validityStart);
  }, [validityStart.getTime()]);

  React.useEffect(() => {
    setLocalEndDate(validityEnd);
  }, [validityEnd.getTime()]);

  /**
   * If the validity period is being enabled and the start or end dates are still set to their default values, update them to the current date.
   * @param {boolean} checked
   */
  const fixStartEndOldValuesIfNeededAndSetValidity = checked => {
    setValidity(checked);

    const now = new Date();

    if (checked && localStartDate.getFullYear() === LEGACY_START_YEAR) {
      holdUntilValid(now, changeStartTs);
    }

    if (checked && localEndDate.getFullYear() === LEGACY_END_YEAR) {
      holdUntilValid(now, changeEndTs);
    }
  };

  /**
   * Ensures that only valid dates are passed to the change handlers, preventing issues with invalid date objects from the date picker.
   * @param {Date} date
   * @param {function(Date): void} changeHandler
   */
  const holdUntilValid = (date, changeHandler) => {
    // invalid date is also a date object, but isNaN so we can filter with that
    if (date && !Number.isNaN(date.getTime())) {
      changeHandler(date);
    }
  };

  return (
    <FormGroup>
      <ControlLabel>Validity Period</ControlLabel>
      <div>
        <Checkbox
          data-test="checkbox-validity"
          name="validityEnabled"
          checked={enabled}
          onChange={e => fixStartEndOldValuesIfNeededAndSetValidity(e.target.checked)}
          className="input-0-mg flex justify-center items-center"
        >
          Enable
        </Checkbox>
        {enabled && (
          <div className="datepicker-validity" data-test="datepicker-validity">
            <DatePicker
              className="form-control input-sm"
              dateFormat={DATE_FORMAT}
              timeFormat={TIME_FORMAT}
              selected={localStartDate}
              selectsStart
              startDate={localStartDate}
              endDate={localEndDate}
              onChange={date => {
                setLocalStartDate(date);
                holdUntilValid(date, changeStartTs);
              }}
              placeholderText="Start date"
              todayButton="Today"
              showTimeInput
              withPortal={withPortal}
            />
            <DatePicker
              className="form-control input-sm"
              dateFormat={DATE_FORMAT}
              timeFormat={TIME_FORMAT}
              selected={localEndDate}
              selectsEnd
              startDate={localStartDate}
              endDate={localEndDate}
              onChange={date => {
                setLocalEndDate(date);
                holdUntilValid(date, changeEndTs);
              }}
              placeholderText="End date"
              todayButton="Today"
              showTimeInput
              withPortal={withPortal}
            />
          </div>
        )}
      </div>
    </FormGroup>
  );
}

ValidityFormComponent.propTypes = {
  enabled: PropTypes.bool.isRequired,
  setValidity: PropTypes.func.isRequired,
  validityStart: PropTypes.instanceOf(Date).isRequired,
  changeStartTs: PropTypes.func.isRequired,
  validityEnd: PropTypes.instanceOf(Date).isRequired,
  changeEndTs: PropTypes.func.isRequired,
  withPortal: PropTypes.bool,
};

export default ValidityFormComponent;
